import { MouseEvent, useCallback } from 'react';
import { UseModalReturnProps } from './props';
import { useSearchParams } from '../../useSearchParams';
import { usePathname } from '../../usePathname';
import { NavigateFunction } from '@starsoft/common/interfaces/navigate-function';
import { useNavigate } from '../../useNavigate';
import { useHash } from '../../useHash';

/**
 * Custom hook to manage modal state and navigation.
 * This hook provides functionality to determine if a modal is open, close the modal, handle container events, and set search parameters.
 *
 * @param {string} modalKey - The key representing the modal in the URL hash.
 * @returns {UseModalReturnProps} - An object containing the modal state and handlers.
 *
 * Example usage:
 * const { isOpen, onClose, handleContainer, setSearchParams } = useModal('myModalKey');
 */
export function useModal(modalKey: string): UseModalReturnProps {
  // Hook to manage URL search parameters
  const search: URLSearchParams = useSearchParams();
  // Hook to get the current pathname
  const pathname: string = usePathname();
  // Hook to get the current URL hash
  const hash: string = useHash();
  // Determine if the modal is open based on the URL hash
  const isOpen: boolean = hash?.includes(`#${modalKey}`);
  // Hook to navigate between routes
  const navigate: NavigateFunction = useNavigate();

  /**
   * Callback function to close the modal.
   * This function navigates to the current pathname and optionally clears search parameters.
   *
   * @param {boolean} [clearParams=true] - Flag to clear search parameters when closing the modal.
   */
  const onClose = useCallback(
    (clearParams: boolean = true) => {
      navigate(
        {
          pathname,
          search: clearParams ? undefined : search.toString(),
        },
        {
          preventScrollReset: true,
        },
      );
    },
    [pathname, search, navigate],
  );

  /**
   * Callback function to set search parameters and open the modal.
   * This function updates the search parameters and sets the modal key in the URL hash.
   *
   * @template T - The type of the value to set in the search parameters.
   * @param {string} searchParamKey - The key of the search parameter to set.
   * @param {T} value - The value to set for the search parameter.
   */
  const setSearchParams = useCallback(
    <T,>(searchParamKey: string, value: T) => {
      search.set(searchParamKey, `${value}`);
      navigate(
        {
          search: search.toString(),
          hash: modalKey,
        },
        {
          preventScrollReset: true,
        },
      );
    },
    [search, modalKey, navigate],
  );

  /**
   * Function to handle container events.
   * This function stops the propagation of the event to prevent closing the modal when clicking inside the container.
   *
   * @param {MouseEvent<HTMLDivElement>} event - The mouse event triggered by clicking inside the container.
   */
  function handleContainer(event: MouseEvent<HTMLDivElement>): void {
    event.stopPropagation();
  }

  // Return an object containing the modal state and handlers
  return {
    isOpen,
    onClose,
    handleContainer,
    setSearchParams,
  };
}

export type { UseModalReturnProps };
